import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetTravellerByIDQueryKey,
  getGetTravellersPerOrganizationQueryKey,
  getSelfServiceListBearersQueryKey,
  useSelfServiceSetTravellerFavoredBearer,
  useSetTravellerFavoredBearer,
} from '@/api/trafikportalen';
import { getApiErrorMessage } from '@/helpers/apiError';
import { toast } from '@/hooks/useToast';
import type {
  SelfServiceBearersResponseDTO,
  TravellerWithCollectionMembershipsDTO,
} from '@/api/trafikportalen';

const isDepartmentTravellersQuery = (queryKey: readonly unknown[]) => {
  const [path] = queryKey;
  return (
    typeof path === 'string' && /^\/departments\/[^/]+\/travellers$/.test(path)
  );
};

const withFavoredBearer = (
  entry: TravellerWithCollectionMembershipsDTO,
  travellerId: string,
  mtbBearerId: string,
): TravellerWithCollectionMembershipsDTO => {
  if (entry.traveller.id !== travellerId) {
    return entry;
  }

  return {
    ...entry,
    traveller: {
      ...entry.traveller,
      favored_bobcat_bearer_id: mtbBearerId,
    },
  };
};

interface UseFavoredBearerProps {
  travellerId: string;
  isSelfService?: boolean;
}

interface SetFavoredBearerOptions {
  showToast?: boolean;
}

export const useFavoredBearer = ({
  travellerId,
  isSelfService = false,
}: UseFavoredBearerProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const setAdminFavoredBearer = useSetTravellerFavoredBearer();
  const setSelfServiceFavoredBearer = useSelfServiceSetTravellerFavoredBearer();

  const mutateAsync = isSelfService
    ? setSelfServiceFavoredBearer.mutateAsync
    : setAdminFavoredBearer.mutateAsync;
  const isPending = isSelfService
    ? setSelfServiceFavoredBearer.isPending
    : setAdminFavoredBearer.isPending;

  const syncFavoredBearerCache = useCallback(
    (mtbBearerId: string) => {
      if (isSelfService) {
        queryClient.setQueryData<SelfServiceBearersResponseDTO>(
          getSelfServiceListBearersQueryKey(),
          (current) => {
            if (!current) {
              return current;
            }

            const patchEntries = (
              entries: SelfServiceBearersResponseDTO['self'],
            ) =>
              entries.map((entry) =>
                entry.traveller_id === travellerId
                  ? { ...entry, favored_bobcat_bearer_id: mtbBearerId }
                  : entry,
              );

            return {
              self: patchEntries(current.self),
              wards: patchEntries(current.wards),
            };
          },
        );
        return;
      }

      queryClient.setQueryData<TravellerWithCollectionMembershipsDTO>(
        getGetTravellerByIDQueryKey(travellerId),
        (current) =>
          current
            ? withFavoredBearer(current, travellerId, mtbBearerId)
            : current,
      );

      const patchList = (
        current: TravellerWithCollectionMembershipsDTO[] | undefined,
      ) =>
        current?.map((entry) =>
          withFavoredBearer(entry, travellerId, mtbBearerId),
        );

      queryClient.setQueryData(
        getGetTravellersPerOrganizationQueryKey(),
        patchList,
      );
      queryClient.setQueriesData<TravellerWithCollectionMembershipsDTO[]>(
        { predicate: (query) => isDepartmentTravellersQuery(query.queryKey) },
        patchList,
      );
    },
    [isSelfService, queryClient, travellerId],
  );

  const invalidateFavoredBearer = useCallback(async () => {
    if (isSelfService) {
      await queryClient.invalidateQueries({
        queryKey: getSelfServiceListBearersQueryKey(),
      });
      return;
    }

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetTravellerByIDQueryKey(travellerId),
      }),
      queryClient.invalidateQueries({
        queryKey: getGetTravellersPerOrganizationQueryKey(),
      }),
      queryClient.invalidateQueries({
        predicate: (query) => isDepartmentTravellersQuery(query.queryKey),
      }),
    ]);
  }, [isSelfService, queryClient, travellerId]);

  const setFavoredBearer = useCallback(
    async (mtbBearerId: string, options?: SetFavoredBearerOptions) => {
      await mutateAsync({
        travellerID: travellerId,
        data: { mtb_bearer_id: mtbBearerId },
      });

      syncFavoredBearerCache(mtbBearerId);
      await invalidateFavoredBearer();

      if (options?.showToast === false) {
        return;
      }

      toast.success(
        {
          title: formatMessage({
            defaultMessage: 'Föredragen bärare uppdaterad',
          }),
        },
        { autoClose: 3000 },
      );
    },
    [
      formatMessage,
      invalidateFavoredBearer,
      mutateAsync,
      syncFavoredBearerCache,
      travellerId,
    ],
  );

  const getFavoredBearerErrorMessage = useCallback(
    (error: unknown) =>
      getApiErrorMessage(
        error,
        formatMessage({
          defaultMessage:
            'Försök spara igen senare, eller vänligen kontakta support.',
        }),
      ),
    [formatMessage],
  );

  return {
    setFavoredBearer,
    getFavoredBearerErrorMessage,
    isPending,
  };
};
